import React, { useEffect, useState } from 'react';
import styles from './ChatFooter.module.css';

type ChatFooterProps = {
  fetchResponse: (userMessage: string) => void;
  isGenerating?: boolean;
  onCancelGeneration?: () => void;
  prefillText?: string;
  /** Several, not one: two documents can be compared in a single turn. */
  attachments?: { name: string; truncated?: boolean }[];
  attaching?: boolean;
  attachError?: string | null;
  /** Bearer token, used to fetch the caller's plan attachment limits (SRS #34). */
  token?: string | null;
  onAttach?: (files: File[]) => void;
  /** Remove one attachment by index, or all of them when index is omitted. */
  onClearAttachment?: (index?: number) => void;
};

export const ChatFooter: React.FC<ChatFooterProps> = ({
  fetchResponse,
  isGenerating = false,
  onCancelGeneration = () => {},
  prefillText,
  attachments = [],
  attaching = false,
  attachError = null,
  token = null,
  onAttach,
  onClearAttachment,
}) => {
  const [userMessage, setUserMessage] = React.useState<string>("");
  const lastSubmittedPrefill = React.useRef<string | undefined>(undefined);
  type Limits = { tier: string | null; max_file_mb: number; max_files_per_day: number | null; allowed_types: string[]; used_today: number };
  const [limits, setLimits] = useState<Limits | null>(null);
  useEffect(() => {
    if (!onAttach || !token) return;
    const apiUrl = process.env.REACT_APP_API_BASE_URL ?? '';
    let cancelled = false;
    fetch(`${apiUrl}/api/v1/chat/user_chat/attachment-limits`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setLimits(d); })
      .catch(() => {});
    return () => { cancelled = true; };
    // re-fetch after each attachment so "n of N today" stays right
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, attachments.length]);
  const accept = limits && limits.allowed_types.length ? limits.allowed_types.map((t) => `.${t}`).join(',') : '.pdf,.txt,.md,.csv,.json,.html';
  const quotaExhausted = !!limits && limits.max_files_per_day !== null && limits.used_today >= limits.max_files_per_day;
  const noPlan = !!limits && limits.max_file_mb === 0;
  const limitsHint = !limits ? 'Attach a document (PDF or text) to analyse'
    : noPlan ? 'Attachments are not included in your plan'
    : `${limits.allowed_types.map((t) => t.toUpperCase()).join('/')} up to ${limits.max_file_mb} MB` + (limits.max_files_per_day !== null ? ` · ${limits.used_today} of ${limits.max_files_per_day} today` : '');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Prefill comes only from "run this prompt" entry points — the dashboard
  // quick-start suggestion cards and trending-topic ?q= links. Those should
  // AUTO-SUBMIT, not just drop text in the box for the user to click Send
  // (meeting issue: "menu options just move to the chat window and the user
  // has to click submit"). Guard against re-submitting the same prompt and
  // against interrupting an in-flight generation.
  React.useEffect(() => {
    const text = (prefillText || "").trim();
    if (!text) return;
    if (isGenerating) { setUserMessage(prefillText || ""); return; }
    if (text === lastSubmittedPrefill.current) return;
    lastSubmittedPrefill.current = text;
    setUserMessage("");
    fetchResponse(text);
  }, [prefillText, isGenerating]);  // eslint-disable-line react-hooks/exhaustive-deps
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserMessage(e.target.value);
  };

  const handleSubmit = () => {
    if (!userMessage.trim() || isGenerating) return;
    const message = userMessage.trim();
    setUserMessage("");
    fetchResponse(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.inputContainer}>
        <div className={styles.inputWrapper}>
          {/* Sparkles icon — reference: 16px blue icon left of input */}
          <span className={styles.inputSpark} aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5z" />
              <path d="M19 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1z" />
              <path d="M5 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1z" />
            </svg>
          </span>

          {/* Attach a document for analysis (QA #3). Placed on the LEFT of the
              composer (before the input) so it's clearly visible to users. */}
          {onAttach && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                style={{ display: 'none' }}
                multiple
                onChange={(e) => {
                  const picked = Array.from(e.target.files ?? []);
                  if (picked.length) onAttach(picked);
                  e.target.value = '';
                }}
              />
              {attachments.length > 0 ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', flexShrink: 0 }}
                      data-testid="chat-attachments">
                  {attachments.map((a, i) => (
                    <span
                      key={`${a.name}-${i}`}
                      title={a.truncated ? 'Large document — analysed up to the first part' : a.name}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 160, fontSize: 12, padding: '2px 8px', borderRadius: 999, background: 'var(--ds-accent-soft, #eef0ff)', color: 'var(--ds-accent, var(--ds-brand-blue))', flexShrink: 0 }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📎 {a.name}</span>
                      <button type="button" onClick={() => onClearAttachment?.(i)}
                              aria-label={`Remove ${a.name}`}
                              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', fontSize: 13, lineHeight: 1 }}>✕</button>
                    </span>
                  ))}
                  {/* Still room for another: the limit is per message, not one. */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={attaching || isGenerating || quotaExhausted || noPlan}
                    aria-label="Attach another document"
                    data-testid="attach-another"
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ds-text-muted)', fontSize: 12, padding: '2px 4px' }}
                  >+ add</button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attaching || isGenerating || quotaExhausted || noPlan}
                  title={attachError || (quotaExhausted ? `Daily attachment limit reached (${limits!.max_files_per_day}) — upgrade for more` : limitsHint)}
                  aria-label="Attach document"
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: attachError ? 'var(--ds-danger)' : 'var(--ds-text-muted, var(--ds-text-muted))', padding: 4, opacity: attaching ? 0.5 : 1, flexShrink: 0 }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                </button>
              )}
            </>
          )}

          <textarea
            ref={textAreaRef}
            value={userMessage}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Ask about any regulation, policy or jurisdiction..."
            className={styles.input}
            disabled={isGenerating}
            rows={1}
          />

          <div className={styles.trailingActions}>
            {!isGenerating ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!userMessage.trim()}
                className={styles.sendButton}
                aria-label="Send message"
              >
                {/* Reference uses an upward arrow icon */}
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.4"
                     strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="19" x2="12" y2="5" />
                  <polyline points="5 12 12 5 19 12" />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                onClick={onCancelGeneration}
                className={styles.stopButton}
                title="Stop generating"
                aria-label="Stop generating"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};
